// Veyl — kjerne-algoritmer for konturbasert pikselering
// Alt kjører 100 % i nettleseren. Ingen server-kostnad.

/**
 * Pixelate a region of a canvas inside an arbitrary polygon (natural contour).
 * Uses downscale + nearest-neighbor upscale for "8x8-style" destructive pixelation.
 *
 * @param {HTMLCanvasElement} sourceCanvas - canvas med originalt bilde
 * @param {Array<{x:number,y:number}>} polygon - polygon i canvas-koordinater
 * @param {number} blockSize - piksel-blokk-størrelse (4, 8, 16). Mindre = sikrere.
 * @param {'pixelate'|'blackbox'} mode - hvordan sladde
 */
export function pixelatePolygon(sourceCanvas, polygon, blockSize = 8, mode = "pixelate") {
  if (!polygon || polygon.length < 3) return;
  const ctx = sourceCanvas.getContext("2d");

  // 1. Finn bounding box av polygonet
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  minX = Math.max(0, Math.floor(minX));
  minY = Math.max(0, Math.floor(minY));
  maxX = Math.min(sourceCanvas.width, Math.ceil(maxX));
  maxY = Math.min(sourceCanvas.height, Math.ceil(maxY));
  const w = maxX - minX;
  const h = maxY - minY;
  if (w <= 0 || h <= 0) return;

  if (mode === "blackbox") {
    // Fyll polygon med heldekkende svart
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(polygon[0].x, polygon[0].y);
    for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i].x, polygon[i].y);
    ctx.closePath();
    ctx.fillStyle = "#000";
    ctx.fill();
    ctx.restore();
    return;
  }

  // 2. Kopier området til et lite canvas
  const small = document.createElement("canvas");
  const scale = Math.max(1, Math.round(Math.min(w, h) / blockSize));
  // Vi vil ha ca "blockSize" piksler over minste dimensjon
  const smallW = Math.max(2, Math.round(w / scale));
  const smallH = Math.max(2, Math.round(h / scale));
  small.width = smallW;
  small.height = smallH;
  const sctx = small.getContext("2d");
  sctx.imageSmoothingEnabled = true; // gjennomsnittsfarge per blokk (downscale)
  sctx.drawImage(sourceCanvas, minX, minY, w, h, 0, 0, smallW, smallH);

  // 3. Klipp destinasjon til polygonet (så pikselering kun havner innenfor konturen)
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(polygon[0].x, polygon[0].y);
  for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i].x, polygon[i].y);
  ctx.closePath();
  ctx.clip();

  // 4. Skaler opp igjen med "nearest neighbor" (skarpe blokker, ingen blur)
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(small, 0, 0, smallW, smallH, minX, minY, w, h);
  ctx.restore();
}

/**
 * Pixelate multiple polygons on one canvas.
 */
export function pixelatePolygons(canvas, polygons, blockSize = 8, mode = "pixelate") {
  for (const poly of polygons) {
    pixelatePolygon(canvas, poly, blockSize, mode);
  }
}

/**
 * Utvider et polygon utover (positive offset) — enkel implementasjon (skalering mot senter).
 * Brukes for å legge en liten "margin" rundt ansiktet så sladden dekker helt.
 */
export function expandPolygon(polygon, factor = 1.08) {
  if (!polygon || polygon.length < 3) return polygon;
  let cx = 0, cy = 0;
  for (const p of polygon) { cx += p.x; cy += p.y; }
  cx /= polygon.length;
  cy /= polygon.length;
  return polygon.map((p) => ({
    x: cx + (p.x - cx) * factor,
    y: cy + (p.y - cy) * factor,
  }));
}

/**
 * Konverter MediaPipe FaceMesh landmarks til et ansikts-omriss-polygon.
 * MediaPipe leverer 468 landmarks. Ytterkanten av ansiktet dekkes av
 * FACEMESH_FACE_OVAL-punkter (36 stk). Vi bruker disse for konturen.
 */
export const FACE_OVAL_INDICES = [
  10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288,
  397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136,
  172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
];

export function landmarksToPolygon(landmarks, canvasW, canvasH, indices = FACE_OVAL_INDICES) {
  return indices.map((i) => ({
    x: landmarks[i].x * canvasW,
    y: landmarks[i].y * canvasH,
  }));
}

/**
 * Last inn MediaPipe FaceLandmarker asynkront. Cache modell-instansen.
 */
let _faceLandmarker = null;
let _faceLandmarkerPromise = null;

export async function loadFaceLandmarker() {
  if (_faceLandmarker) return _faceLandmarker;
  if (_faceLandmarkerPromise) return _faceLandmarkerPromise;
  _faceLandmarkerPromise = (async () => {
    const { FilesetResolver, FaceLandmarker } = await import("@mediapipe/tasks-vision");
    const filesetResolver = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm"
    );
    const lm = await FaceLandmarker.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
        delegate: "GPU",
      },
      runningMode: "IMAGE",
      numFaces: 10,
    });
    _faceLandmarker = lm;
    return lm;
  })();
  return _faceLandmarkerPromise;
}

/**
 * Detekter ansikter i et bilde-element. Returnerer array av polygoner.
 */
export async function detectFaces(imageEl, canvasW, canvasH) {
  const lm = await loadFaceLandmarker();
  const result = lm.detect(imageEl);
  if (!result.faceLandmarks || result.faceLandmarks.length === 0) return [];
  return result.faceLandmarks.map((lms) =>
    landmarksToPolygon(lms, canvasW, canvasH)
  );
}

// ============================================================================
// KROPPS-/HUD-SEGMENTERING via MediaPipe ImageSegmenter (selfie_multiclass)
// Klasser:
//   0 = BACKGROUND, 1 = HAIR, 2 = BODY_SKIN, 3 = FACE_SKIN, 4 = CLOTHES, 5 = OTHERS
// ============================================================================
let _imageSegmenter = null;
let _imageSegmenterPromise = null;

export async function loadImageSegmenter() {
  if (_imageSegmenter) return _imageSegmenter;
  if (_imageSegmenterPromise) return _imageSegmenterPromise;
  _imageSegmenterPromise = (async () => {
    const { FilesetResolver, ImageSegmenter } = await import("@mediapipe/tasks-vision");
    const filesetResolver = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm"
    );
    const seg = await ImageSegmenter.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/1/selfie_multiclass_256x256.tflite",
        delegate: "GPU",
      },
      runningMode: "IMAGE",
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });
    _imageSegmenter = seg;
    return seg;
  })();
  return _imageSegmenterPromise;
}

/**
 * Detekter hud/kropp i bildet. Returnerer en maske-canvas (samme størrelse som input)
 * hvor hvite piksler = området som skal pikseleres.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|HTMLVideoElement} imageEl
 * @param {number} canvasW - bredde på output-masken (samme som canvas)
 * @param {number} canvasH
 * @param {Object} opts - { includeBodySkin, includeFaceSkin, includeHair }
 * @returns {HTMLCanvasElement} maske-canvas (grayscale)
 */
export async function detectSkinMask(imageEl, canvasW, canvasH, opts = {}) {
  const {
    includeBodySkin = true,
    includeFaceSkin = false,
    includeHair = false,
  } = opts;
  const seg = await loadImageSegmenter();
  const result = seg.segment(imageEl);
  if (!result.categoryMask) return null;

  const maskData = result.categoryMask.getAsUint8Array();
  const maskW = result.categoryMask.width;
  const maskH = result.categoryMask.height;

  // Bygg RGBA-mask (samme størrelse som modell-utdata, skalerer opp senere)
  const rgba = new Uint8ClampedArray(maskW * maskH * 4);
  for (let i = 0; i < maskData.length; i++) {
    const cls = maskData[i];
    let include = false;
    if (includeBodySkin && cls === 2) include = true;
    if (includeFaceSkin && cls === 3) include = true;
    if (includeHair && cls === 1) include = true;
    if (include) {
      rgba[i * 4] = 255;
      rgba[i * 4 + 1] = 255;
      rgba[i * 4 + 2] = 255;
      rgba[i * 4 + 3] = 255;
    }
  }

  // Skaler til target-størrelse
  const smallCanvas = document.createElement("canvas");
  smallCanvas.width = maskW;
  smallCanvas.height = maskH;
  const sctx = smallCanvas.getContext("2d");
  const imgData = new ImageData(rgba, maskW, maskH);
  sctx.putImageData(imgData, 0, 0);

  const bigCanvas = document.createElement("canvas");
  bigCanvas.width = canvasW;
  bigCanvas.height = canvasH;
  const bctx = bigCanvas.getContext("2d");
  bctx.imageSmoothingEnabled = false;
  bctx.drawImage(smallCanvas, 0, 0, canvasW, canvasH);

  result.close?.();
  return bigCanvas;
}

/**
 * Anvend pikselering på et canvas KUN der masken er hvit. Bevarer kropps-konturen.
 */
export function pixelateWithMask(canvas, maskCanvas, blockSize = 8, mode = "pixelate") {
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;

  if (mode === "blackbox") {
    // Fyll med svart der masken er hvit
    ctx.save();
    ctx.globalCompositeOperation = "source-atop";
    // Bruk mask som alfa
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tctx = tmp.getContext("2d");
    tctx.drawImage(maskCanvas, 0, 0, w, h);
    tctx.globalCompositeOperation = "source-in";
    tctx.fillStyle = "#000";
    tctx.fillRect(0, 0, w, h);
    ctx.drawImage(tmp, 0, 0);
    ctx.restore();
    return;
  }

  // 1. Lag pikselert versjon av HELE bildet
  const scale = Math.max(2, Math.round(Math.min(w, h) / blockSize));
  const smallW = Math.max(2, Math.round(w / scale));
  const smallH = Math.max(2, Math.round(h / scale));
  const small = document.createElement("canvas");
  small.width = smallW;
  small.height = smallH;
  const sctx = small.getContext("2d");
  sctx.imageSmoothingEnabled = true;
  sctx.drawImage(canvas, 0, 0, w, h, 0, 0, smallW, smallH);

  const pixelated = document.createElement("canvas");
  pixelated.width = w;
  pixelated.height = h;
  const pctx = pixelated.getContext("2d");
  pctx.imageSmoothingEnabled = false;
  pctx.drawImage(small, 0, 0, smallW, smallH, 0, 0, w, h);

  // 2. Klipp pikselert versjon til masken
  pctx.globalCompositeOperation = "destination-in";
  pctx.drawImage(maskCanvas, 0, 0, w, h);

  // 3. Legg over original
  ctx.drawImage(pixelated, 0, 0);
}

/**
 * Kombiner flere polygoner til én maske-canvas.
 */
export function polygonsToMask(canvasW, canvasH, polygons) {
  const mask = document.createElement("canvas");
  mask.width = canvasW;
  mask.height = canvasH;
  const ctx = mask.getContext("2d");
  ctx.fillStyle = "#fff";
  for (const poly of polygons) {
    if (poly.length < 3) continue;
    ctx.beginPath();
    ctx.moveTo(poly[0].x, poly[0].y);
    for (let i = 1; i < poly.length; i++) ctx.lineTo(poly[i].x, poly[i].y);
    ctx.closePath();
    ctx.fill();
  }
  return mask;
}

/**
 * Kombiner to masker (union) — bruk for polygoner + hud-maske sammen.
 */
export function combineMasks(maskA, maskB) {
  if (!maskA) return maskB;
  if (!maskB) return maskA;
  const w = Math.max(maskA.width, maskB.width);
  const h = Math.max(maskA.height, maskB.height);
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  ctx.drawImage(maskA, 0, 0, w, h);
  ctx.drawImage(maskB, 0, 0, w, h);
  return out;
}

/**
 * Intersect to masker — bare der begge er hvite forblir hvitt.
 * Brukes når skaperen vil pikelere KUN hud INNENFOR markert område.
 */
export function intersectMasks(maskA, maskB) {
  if (!maskA || !maskB) return null;
  const w = Math.max(maskA.width, maskB.width);
  const h = Math.max(maskA.height, maskB.height);
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const ctx = out.getContext("2d");
  ctx.drawImage(maskA, 0, 0, w, h);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(maskB, 0, 0, w, h);
  return out;
}

// ============================================================================
// TEMPLATE-TRACKING — følger et brukertegnet område gjennom en video.
// Bruker gråtone-baserte SSD (Sum-of-Squared-Differences) template-matching.
// Ingen eksterne bibliotek. Fungerer bra for langsom-moderat bevegelse.
// ============================================================================

/**
 * Beregn centroide av et polygon.
 */
export function polygonCentroid(points) {
  let cx = 0, cy = 0;
  for (const p of points) { cx += p.x; cy += p.y; }
  return { x: cx / points.length, y: cy / points.length };
}

/**
 * Flytt et polygon med (dx, dy).
 */
export function translatePolygon(points, dx, dy) {
  return points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}

/**
 * Ekstraher en gråtone-template rundt punktet (cx, cy) med gitt størrelse.
 * Returnerer null hvis punktet er for nær kanten.
 */
export function extractTemplate(canvas, cx, cy, size = 40) {
  const half = Math.floor(size / 2);
  const x = Math.floor(cx - half);
  const y = Math.floor(cy - half);
  if (x < 0 || y < 0 || x + size > canvas.width || y + size > canvas.height) {
    // Prøv å beskjære innenfor
    const nx = Math.max(0, Math.min(canvas.width - size, x));
    const ny = Math.max(0, Math.min(canvas.height - size, y));
    if (canvas.width < size || canvas.height < size) return null;
    return extractTemplateAt(canvas, nx, ny, size, size);
  }
  return extractTemplateAt(canvas, x, y, size, size);
}

function extractTemplateAt(canvas, x, y, w, h) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const imgData = ctx.getImageData(x, y, w, h);
  const gray = new Uint8Array(w * h);
  const d = imgData.data;
  for (let i = 0; i < w * h; i++) {
    gray[i] = (d[i * 4] + d[i * 4 + 1] + d[i * 4 + 2]) / 3;
  }
  return { data: gray, width: w, height: h };
}

/**
 * Finn beste match av template i canvas rundt (prevCx, prevCy).
 * Returnerer {cx, cy, scale, score} eller null hvis ingen god match.
 *
 * Optimalisert med sub-piksel-søk og valgfri multi-skala.
 * @param {Object} opts - { searchRadius=32, scales=[1.0], samplePixels=true }
 */
export function findTemplate(canvas, template, prevCx, prevCy, opts = {}) {
  if (!template) return null;
  const {
    searchRadius = 24,
    scales = [0.92, 1.0, 1.08],
    step = 1,
    sampleStride = 1,
  } = opts;

  const tw = template.width;
  const th = template.height;
  const halfW = Math.floor(tw / 2);
  const halfH = Math.floor(th / 2);

  // Vi trenger maks-skala-plass rundt sentrum for å lese
  const maxScale = Math.max(...scales);
  const scaledMaxW = Math.ceil(tw * maxScale);
  const scaledMaxH = Math.ceil(th * maxScale);
  const scaledMaxHalfW = Math.floor(scaledMaxW / 2);
  const scaledMaxHalfH = Math.floor(scaledMaxH / 2);

  const sx = Math.max(0, Math.floor(prevCx - searchRadius - scaledMaxHalfW));
  const sy = Math.max(0, Math.floor(prevCy - searchRadius - scaledMaxHalfH));
  const sw = Math.min(canvas.width - sx, 2 * searchRadius + scaledMaxW);
  const sh = Math.min(canvas.height - sy, 2 * searchRadius + scaledMaxH);
  if (sw < scaledMaxW || sh < scaledMaxH) return null;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const imgData = ctx.getImageData(sx, sy, sw, sh);
  const gray = new Uint8Array(sw * sh);
  const d = imgData.data;
  for (let i = 0; i < sw * sh; i++) {
    gray[i] = (d[i * 4] + d[i * 4 + 1] + d[i * 4 + 2]) / 3;
  }

  let bestX = 0, bestY = 0, bestScore = Infinity, bestScale = 1.0;

  for (const scale of scales) {
    const stw = Math.round(tw * scale);
    const sth = Math.round(th * scale);
    if (stw >= sw || sth >= sh) continue;

    for (let dy = 0; dy <= sh - sth; dy += step) {
      for (let dx = 0; dx <= sw - stw; dx += step) {
        let ssd = 0;
        for (let ty = 0; ty < th; ty += sampleStride) {
          for (let tx = 0; tx < tw; tx += sampleStride) {
            const stx = Math.min(stw - 1, Math.round(tx * scale));
            const sty = Math.min(sth - 1, Math.round(ty * scale));
            const v1 = gray[(dy + sty) * sw + (dx + stx)];
            const v2 = template.data[ty * tw + tx];
            const diff = v1 - v2;
            ssd += diff * diff;
            if (ssd >= bestScore) break;
          }
          if (ssd >= bestScore) break;
        }
        if (ssd < bestScore) {
          bestScore = ssd;
          bestX = dx;
          bestY = dy;
          bestScale = scale;
        }
      }
    }
  }

  const stw = Math.round(tw * bestScale);
  const sth = Math.round(th * bestScale);
  return {
    cx: sx + bestX + Math.floor(stw / 2),
    cy: sy + bestY + Math.floor(sth / 2),
    scale: bestScale,
    score: bestScore,
    // Normalized score per sample count for adaptive threshold
    normScore: bestScore / (Math.ceil(tw / sampleStride) * Math.ceil(th / sampleStride)),
  };
}

/**
 * Adaptivt oppdaterer template — blend inn litt av det nye område per frame.
 * Blender 15 % nytt inn i eksisterende template.
 */
export function blendTemplate(oldTemplate, canvas, cx, cy, blendFactor = 0.15) {
  const fresh = extractTemplate(canvas, cx, cy, oldTemplate.width);
  if (!fresh || fresh.width !== oldTemplate.width || fresh.height !== oldTemplate.height) {
    return oldTemplate;
  }
  const newData = new Uint8Array(oldTemplate.data.length);
  for (let i = 0; i < oldTemplate.data.length; i++) {
    newData[i] = Math.round(oldTemplate.data[i] * (1 - blendFactor) + fresh.data[i] * blendFactor);
  }
  return { data: newData, width: oldTemplate.width, height: oldTemplate.height };
}

/**
 * Skaler et polygon fra dets centroide med gitt faktor. Brukes for å tilpasse
 * størrelse under multi-skala tracking, og for auto-margin.
 */
export function scalePolygon(points, factor, center) {
  const c = center || polygonCentroid(points);
  return points.map((p) => ({
    x: c.x + (p.x - c.x) * factor,
    y: c.y + (p.y - c.y) * factor,
  }));
}
